import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, ApiError } from "../../lib/api-client";
import { PasswordField } from "../../components/PasswordField";

interface TenantLoginResponse {
  ok: boolean;
  token: string;
  user: {
    id: string;
    username: string;
    role: string;
    tenantId: string;
    tenantSlug: string;
    tenantTradeName?: string;
  };
}

interface OtpRequestResponse {
  ok: boolean;
  tenantSlug?: string;
  message?: string;
  demoOtp?: string;
}

export function TenantLoginForm() {
  const [tab, setTab] = useState<"manager" | "wildcard" | "otp">("manager");
  const navigate = useNavigate();

  // Estado Gestor Corporativo (Username + Password via JWT)
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerError, setManagerError] = useState<string | null>(null);

  // Estado Colaborador (Código Corporativo Curinga)
  const [wildcardEmail, setWildcardEmail] = useState("");
  const [wildcardCode, setWildcardCode] = useState("");
  const [wildcardLoading, setWildcardLoading] = useState(false);
  const [wildcardError, setWildcardError] = useState<string | null>(null);

  // Estado Colaborador (OTP por E-mail Institucional)
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"request" | "verify">("request");
  const [otpMessage, setOtpMessage] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);

  async function handleManagerLogin(e: React.FormEvent) {
    e.preventDefault();
    setManagerLoading(true);
    setManagerError(null);

    try {
      const res = await apiClient.post<TenantLoginResponse>("/tenant/auth/login", {
        username,
        password,
      });

      if (res.token) {
        localStorage.setItem("datasci_tenant_token", res.token);
        localStorage.setItem("datasci_tenant_user", JSON.stringify(res.user));
        navigate("/t-admin/painel");
      }
    } catch (err) {
      setManagerError(err instanceof ApiError ? err.message : "Credenciais corporativas inválidas.");
    } finally {
      setManagerLoading(false);
    }
  }

  async function handleWildcardLogin(e: React.FormEvent) {
    e.preventDefault();
    setWildcardLoading(true);
    setWildcardError(null);

    try {
      const res = await apiClient.post<{ ok: boolean; tenantSlug?: string }>("/tenant/auth/wildcard", {
        email: wildcardEmail.trim(),
        code: wildcardCode.trim(),
      });

      if (res.ok) {
        window.location.href = res.tenantSlug ? `/t/${res.tenantSlug}` : "/minha-biblioteca";
      }
    } catch (err) {
      setWildcardError(err instanceof ApiError ? err.message : "Código corporativo inválido ou expirado.");
    } finally {
      setWildcardLoading(false);
    }
  }

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setOtpLoading(true);
    setOtpError(null);
    setOtpMessage(null);
    try {
      const res = await apiClient.post<OtpRequestResponse>("/tenant/auth/otp/request", { email: email.trim() });
      setStep("verify");
      setOtpMessage(res.message || "Código enviado por e-mail. Confira sua caixa de entrada.");
      if (res.demoOtp) {
        setCode(res.demoOtp);
      }
    } catch (err) {
      setOtpError(err instanceof ApiError ? err.message : "Não foi possível enviar o código para este e-mail.");
    } finally {
      setOtpLoading(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    setOtpLoading(true);
    setOtpError(null);
    try {
      const res = await apiClient.post<{ ok: boolean; tenantSlug?: string }>("/tenant/auth/otp/verify", {
        email: email.trim(),
        code: code.trim(),
      });
      setOtpMessage("Login realizado com sucesso.");
      window.location.href = res.tenantSlug ? `/t/${res.tenantSlug}` : "/minha-biblioteca";
    } catch (err) {
      setOtpError(err instanceof ApiError ? err.message : "Código inválido.");
    } finally {
      setOtpLoading(false);
    }
  }

  return (
    <div>
      <h2>Acesso corporativo</h2>

      <div role="tablist" className="tab-group" aria-label="Tipo de Acesso Corporativo" style={{ marginBottom: "var(--space-4)" }}>
        <button
          role="tab"
          type="button"
          aria-selected={tab === "manager"}
          onClick={() => setTab("manager")}
        >
          Gestor (Senha)
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={tab === "wildcard"}
          onClick={() => setTab("wildcard")}
        >
          Código Corporativo
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={tab === "otp"}
          onClick={() => setTab("otp")}
        >
          E-mail Institucional
        </button>
      </div>

      {tab === "manager" && (
        <form onSubmit={handleManagerLogin} className="card">
          <p className="text-muted" style={{ marginBottom: "var(--space-3)", fontSize: "var(--text-sm)" }}>
            Login com usuário/e-mail corporativo e senha para acesso ao painel de gestão de licenças, relatórios e colaboradores.
          </p>

          <div className="field">
            <label htmlFor="tenantUsername">Usuário ou E-mail do Gestor</label>
            <input
              id="tenantUsername"
              type="text"
              required
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="gestor ou gestor@empresa-demo.com.br"
            />
          </div>

          <PasswordField
            id="tenantPassword"
            label="Senha"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />

          {managerError && <p role="alert" className="notice-error">{managerError}</p>}

          <button className="btn" type="submit" disabled={managerLoading} style={{ width: "100%", marginTop: "var(--space-4)" }}>
            {managerLoading ? "Entrando..." : "Acessar Painel do Gestor"}
          </button>

        </form>
      )}

      {tab === "wildcard" && (
        <form onSubmit={handleWildcardLogin} className="card">
          <p className="text-muted" style={{ marginBottom: "var(--space-3)", fontSize: "var(--text-sm)" }}>
            Acesse as obras disponibilizadas pela sua empresa ou universidade utilizando o código corporativo geral fornecido pelo seu gestor.
          </p>

          <div className="field">
            <label htmlFor="wildcard-code">Código de Acesso Corporativo *</label>
            <input
              id="wildcard-code"
              type="text"
              required
              value={wildcardCode}
              onChange={(e) => setWildcardCode(e.target.value.toUpperCase())}
              placeholder="Ex.: DEMO-CORP-2026"
              style={{ textTransform: "uppercase", letterSpacing: "1px" }}
            />
          </div>

          <div className="field" style={{ marginTop: "var(--space-3)" }}>
            <label htmlFor="wildcard-email">Seu E-mail Institucional ou de Colaborador *</label>
            <input
              id="wildcard-email"
              type="email"
              required
              value={wildcardEmail}
              onChange={(e) => setWildcardEmail(e.target.value)}
              placeholder="colaborador@empresa-demo.com.br"
            />
          </div>

          {wildcardError && <p role="alert" className="notice-error">{wildcardError}</p>}

          <button className="btn" type="submit" disabled={wildcardLoading} style={{ width: "100%", marginTop: "var(--space-4)" }}>
            {wildcardLoading ? "Validando acesso..." : "Entrar com Código Corporativo"}
          </button>
        </form>
      )}

      {tab === "otp" && (
        <form onSubmit={step === "request" ? requestCode : verifyCode} className="card">
          <p className="text-muted" style={{ marginBottom: "var(--space-3)", fontSize: "var(--text-sm)" }}>
            Acesso para colaboradores via validação direta do domínio institucional (ex.: <code>@empresa-demo.com.br</code>).
          </p>

          <div className="field">
            <label htmlFor="tenant-email">E-mail Institucional Corporativo</label>
            <input
              id="tenant-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={step === "verify"}
              placeholder="seu.nome@empresa-demo.com.br"
            />
          </div>

          {step === "verify" && (
            <div className="field" style={{ marginTop: "var(--space-3)" }}>
              <label htmlFor="otp-code">Código de Acesso (6 dígitos)</label>
              <input
                id="otp-code"
                type="text"
                inputMode="numeric"
                required
                minLength={4}
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Ex.: 123456"
              />
            </div>
          )}

          {otpError && <p role="alert" className="notice-error">{otpError}</p>}
          {otpMessage && <p role="status" className="notice-success">{otpMessage}</p>}

          <button className="btn" type="submit" disabled={otpLoading} style={{ width: "100%", marginTop: "var(--space-4)" }}>
            {otpLoading ? "Processando…" : step === "request" ? "Verificar E-mail Institucional" : "Confirmar e Acessar Biblioteca"}
          </button>
          {step === "verify" && <button type="button" className="btn btn-secondary" disabled={otpLoading} onClick={() => { setStep("request"); setCode(""); setOtpError(null); setOtpMessage(null); }} style={{ marginTop: "var(--space-3)" }}>Usar outro e-mail</button>}
        </form>
      )}
    </div>
  );
}
